import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { resolveMediaUrl } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Drag-and-drop / click-to-browse uploader.
 *
 * Props:
 *  - token            (admin x-admin-token)
 *  - multiple         (bool — allow batch upload)
 *  - onUploaded(arr)  (called with uploaded media docs)
 *  - children         (optional custom trigger UI)
 */
const MediaUploader = ({ token, multiple = false, onUploaded, children }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const doUpload = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) =>
        ["image/jpeg", "image/png", "image/webp"].includes(f.type)
      );
      if (!files.length) {
        toast.error("Choose JPG, PNG, or WEBP images");
        return;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const res = await axios.post(`${API}/admin/media/upload`, fd, {
          headers: { "x-admin-token": token },
        });
        const added = res.data?.added || [];
        const skipped = res.data?.skipped || [];
        if (added.length) {
          toast.success(`${added.length} image${added.length > 1 ? "s" : ""} uploaded`);
          onUploaded?.(multiple ? added : added[0]);
        }
        skipped.forEach((s) =>
          toast.error(`Skipped ${s.name}: ${s.reason.replace("_", " ")}`)
        );
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [token, onUploaded, multiple]
  );

  if (children) {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple={multiple}
          onChange={(e) => doUpload(e.target.files)}
          className="hidden"
          data-testid="media-file-input"
        />
        <span
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          {typeof children === "function" ? children({ uploading }) : children}
        </span>
      </>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        doUpload(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      data-testid="media-dropzone"
      className={`group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed bg-white px-6 py-8 text-center transition-all ${
        dragging
          ? "border-[#e85a1c] bg-[#fff8f3]"
          : "border-black/15 hover:border-[#e85a1c]/50"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple={multiple}
        onChange={(e) => doUpload(e.target.files)}
        className="hidden"
        data-testid="media-file-input"
      />
      {uploading ? (
        <Loader2 size={28} className="animate-spin text-[#e85a1c]" />
      ) : (
        <div className="rounded-full bg-[#e85a1c]/10 p-3 text-[#e85a1c] transition-transform group-hover:scale-105">
          <Upload size={22} />
        </div>
      )}
      <div className="font-display text-base tracking-tight text-[#1c1c1e]">
        {uploading ? "Uploading…" : "Drop image here or click to browse"}
      </div>
      <div className="flex items-center gap-1 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
        <ImageIcon size={10} /> JPG · PNG · WEBP · ≤ 8 MB
      </div>
    </div>
  );
};

/**
 * Compact preview of a single bound cover image with replace/remove.
 */
export const CoverImagePreview = ({ url, onReplace, onRemove }) => {
  if (!url) return null;
  const absolute = resolveMediaUrl(url);
  return (
    <div className="group relative overflow-hidden rounded-xl border border-black/10 bg-white">
      <img
        src={absolute}
        alt="cover"
        loading="lazy"
        className="block aspect-[16/9] w-full object-cover"
      />
      <div className="absolute inset-0 flex items-end justify-end gap-2 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
        {onReplace && (
          <button
            type="button"
            onClick={onReplace}
            data-testid="cover-replace"
            className="rounded-full bg-white/95 px-3 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-[#1c1c1e] hover:bg-white"
          >
            Replace
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            data-testid="cover-remove"
            className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.2em] text-white hover:bg-red-700"
          >
            <X size={11} /> Remove
          </button>
        )}
      </div>
    </div>
  );
};

export default MediaUploader;
